import React, { memo, useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { toolbarActions } from './toolbarActions';
import { setPostImage, BASE_IMG_URL } from '@images/imageUrls';
import EmojiPicker from '@components/EmojiPicker/pc';
import { useFeedbackAttachmentActions } from '../../../components/FeedbackAttachmentUpload/FeedbackAttachmentContext';
import styles from '../index.module.scss';
import './TiptapEditor.scss';
import { feedbackEditorExtensionsPC } from '../../../components/feedbackEditorExtensions';
import { replaceEmojiCodes } from '../../../../../utils/emoji';
import { prepareEditorHtmlForHydration } from '../../../../../utils/tiptapEditorContent';

export interface TiptapEditorProps {
    initialContent?: string;
    onContentChange?: (content: string) => void;
    emojiList?: any[];
    solvedCount?: number;
    onRelatedClick?: () => void;
}

const TiptapEditor: React.FC<TiptapEditorProps> = ({
    initialContent = '',
    onContentChange,
    emojiList,
    solvedCount = 0,
    onRelatedClick,
}) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [emojiPickerPosition, setEmojiPickerPosition] = useState<{ top: number; left: number }>({ top: 80, left: 0 });
    const onContentChangeRef = useRef(onContentChange);
    const lastHydratedSourceRef = useRef<string | null>(null);
    const lastHydratedHtmlRef = useRef<string>('');
    const toolbarWrapRef = useRef<HTMLDivElement>(null);
    onContentChangeRef.current = onContentChange;

    const {
        hasVideo,
        canUploadVideo,
        openImagePicker,
        openVideoPicker,
    } = useFeedbackAttachmentActions();

    const editor = useEditor({
        extensions: feedbackEditorExtensionsPC,
        content: prepareEditorHtmlForHydration(initialContent),
        onUpdate: ({ editor: ed }) => {
            onContentChangeRef.current?.(ed.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'tiptap-editor-content',
            },
        },
    }, []);

    // 仅在外部内容切换，或编辑器仍停留在上次回填结果时，才同步回编辑器。
    // 这样 emojiList 异步到达时不会覆盖用户刚输入的内容。
    useEffect(() => {
        if (!editor) return;

        const parsedContent = prepareEditorHtmlForHydration(replaceEmojiCodes(initialContent, emojiList));
        const currentHtml = editor.getHTML();
        const sourceChanged = lastHydratedSourceRef.current !== initialContent;
        const editorMatchesLastHydrated = currentHtml === lastHydratedHtmlRef.current;

        if ((sourceChanged || editorMatchesLastHydrated) && currentHtml !== parsedContent) {
            editor.commands.setContent(parsedContent, { parseOptions: { preserveWhitespace: 'full' } });
        }

        lastHydratedSourceRef.current = initialContent;
        lastHydratedHtmlRef.current = parsedContent;
    }, [initialContent, editor, emojiList]);

    const handleEmojiSelect = (emoji: string, emojiCode?: string, meta?: { category?: string }) => {
        if (!editor) return;

        const isDefaultCategory = meta?.category === '默认';
        const defaultEmojiSize = 28;

        if (emoji && emoji.startsWith && emoji.startsWith('http')) {
            const size = isDefaultCategory ? defaultEmojiSize : 59;
            const attrs = isDefaultCategory
                ? ({ alt: emojiCode, src: emoji, 'data-emoji': 'default', width: size, height: size } as any)
                : ({ alt: emojiCode, src: emoji, 'data-emoji': 'large', width: size, height: size } as any);

            const ok = editor.chain().focus().setImage(attrs).insertContent({ type: 'text', text: ' ' }).run();
            if (!ok) {
                editor.chain().focus()
                    .insertContent(`<img src="${emoji}" alt="emoji" data-emoji="${isDefaultCategory ? 'default' : 'large'}" style="width: ${size}px; height: ${size}px; display: inline-block; vertical-align: middle;" />`)
                    .insertContent({ type: 'text', text: ' ' })
                    .run();
            }
        } else {
            editor.chain().focus().insertContent(emoji).insertContent({ type: 'text', text: ' ' }).run();
        }

    };

    const getVideoIcon = () => {
        if (hasVideo || !canUploadVideo) {
            return setPostImage.videoGray;
        }
        return setPostImage.videoBar;
    };

    const updateEmojiPickerPosition = (target: HTMLElement) => {
        const container = toolbarWrapRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const pickerWidth = 375;
        const nextTop = targetRect.bottom - containerRect.top + 12;
        const maxLeft = Math.max(containerRect.width - pickerWidth, 0);
        const nextLeft = Math.min(Math.max(targetRect.left - containerRect.left, 0), maxLeft);

        setEmojiPickerPosition({ top: nextTop, left: nextLeft });
    };

    return (
        <div className={styles.editorOuterWrap}>
            <div className={styles.toolbarWrap} style={{ position: 'relative' }} ref={toolbarWrapRef}>
                <div className={styles.toolbar}>
                    {toolbarActions.map((action) => (
                        <div
                            className={styles.toolItem}
                            key={action.name}
                            onClick={(e) => {
                                if (action.name === 'emoji') {
                                    updateEmojiPickerPosition(e.currentTarget as HTMLElement);
                                    setShowEmojiPicker(!showEmojiPicker);
                                } else if (action.name === 'image') {
                                    openImagePicker();
                                } else if (action.name === 'video') {
                                    openVideoPicker();
                                }
                            }}
                        >
                            <div className={styles.toolIcon}>
                                <img
                                    className={styles.toolImg}
                                    src={
                                        action.name === 'video'
                                            ? getVideoIcon()
                                            : setPostImage[action.icon as keyof typeof setPostImage]
                                    }
                                    alt={action.label}
                                />
                            </div>
                            <div className={styles.toolLabel}>{action.label}</div>
                        </div>
                    ))}
                </div>
                {showEmojiPicker && (
                    <EmojiPicker
                        visible={showEmojiPicker}
                        onClose={() => setShowEmojiPicker(false)}
                        onSelect={handleEmojiSelect}
                        showEmojiTab
                        closeOnSelect={false}
                        position={emojiPickerPosition}
                    />
                )}
            </div>
            {onRelatedClick && (
                <div
                    className={styles.relatedEnter}
                    onClick={onRelatedClick}
                    style={{ cursor: 'pointer' }}
                >
                    <img src={`${BASE_IMG_URL}/feedback/icon9.png`} alt="" className={styles.icon1} />
                    <div className={styles.text}>为你找到已解决相关问题{solvedCount}篇，去查看</div>
                    <img src={`${BASE_IMG_URL}/feedback/icon10.png`} alt="" className={styles.icon2} />
                </div>
            )}
            <div className={styles.tiptapEditorWrap}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
};

export default memo(TiptapEditor);
